import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

type NavChild = { label: string; path: string; desc?: string };
type NavLink = { label: string; path?: string; children?: NavChild[] };

const NAV_LINKS: NavLink[] = [
  { label: 'Home', path: '/' },
  { label: 'Our Services', path: '/services' },
  {
    label: 'Programs',
    children: [
      { label: 'Program Finder', path: '/program-finder', desc: 'Not sure where to start? Find your match' },
      { label: 'In-Home Programs', path: '/in-home-programs', desc: 'Personalized sessions in your own environment' },
    ],
  },
  { label: 'Off-Site Services', path: '/off-site-services' },
  { label: 'Recording Studio', path: '/recording-studio' },
  { label: 'Neurodivergent Creative Development', path: '/neurodivergent-development' },
  { label: 'Call for Pricing', path: 'tel:9102424567' },
  { label: 'Gift Cards', path: '/gift-cards' },
  { label: 'Ministry Connections', path: '/ministry-connections' },
  { label: 'Cities We Serve', path: '/cities-we-serve' },
  { label: 'Blog', path: '/blog' },
  { label: 'Refer a Friend', path: '/refer-a-friend' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Meet the Founder', path: '/meet-the-founder' },
  { label: 'Contact', path: '/contact' },
];

function DropdownNavItem({ link, isLight }: { link: NavLink; isLight: boolean }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const ref = useRef<HTMLLIElement>(null);
  const isActive = link.children?.some((c) => c.path === location.pathname) ?? false;

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  return (
    <li
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 font-body font-medium text-[11px] 2xl:text-xs tracking-wide transition-colors duration-300 relative group cursor-pointer whitespace-nowrap ${
          isLight
            ? 'text-brand-blue-dark hover:text-brand-purple'
            : 'text-white hover:text-brand-yellow'
        } ${isActive ? (isLight ? 'text-brand-purple' : 'text-brand-yellow') : ''}`}
      >
        {link.label}
        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        <span
          className={`absolute -bottom-1 left-0 h-0.5 bg-brand-yellow transition-all duration-300 ${
            isActive ? 'w-full' : 'w-0 group-hover:w-full'
          }`}
        />
      </button>

      {/* Dropdown panel */}
      <div
        className={`absolute top-full left-0 pt-3 z-50 transition-all duration-300 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div
          className="bg-white rounded-2xl overflow-hidden min-w-[240px] border border-gray-100/80"
          style={{ boxShadow: '0 20px 48px -8px rgba(30,58,138,0.18), 0 4px 16px -4px rgba(124,58,237,0.10)' }}
        >
          <div className="p-2">
            {link.children?.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                className={`flex items-start gap-3 px-4 py-3 rounded-xl transition-all duration-200 group/item ${
                  location.pathname === child.path
                    ? 'bg-[#EEF2FF] text-brand-purple'
                    : 'text-brand-blue-dark hover:bg-[#F5F3FF] hover:text-brand-purple'
                }`}
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg mt-0.5 flex-shrink-0 transition-colors duration-200 ${
                  location.pathname === child.path
                    ? 'bg-brand-purple/15 text-brand-purple'
                    : 'bg-brand-blue/8 text-brand-blue-dark group-hover/item:bg-brand-purple/15 group-hover/item:text-brand-purple'
                }`}>
                  <i className={`${
                    child.path === '/program-finder' ? 'ri-search-eye-line' :
                    child.path === '/on-site-programs' ? 'ri-building-line' :
                    child.path === '/walk-in' ? 'ri-door-open-line' : 'ri-home-heart-line'
                  } text-sm`} />
                </div>
                <div>
                  <p className="font-body font-semibold text-xs leading-snug">{child.label}</p>
                  {child.desc && (
                    <p className="font-body text-[10px] text-gray-400 mt-0.5 leading-snug">{child.desc}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </li>
  );
}

function SimpleNavItem({ link, isLight }: { link: NavLink; isLight: boolean }) {
  const location = useLocation();
  if (!link.path) return null;

  const isTel = link.path.startsWith('tel:');
  if (isTel) {
    return (
      <li>
        <a
          href={link.path}
          className={`font-body font-semibold text-[11px] 2xl:text-xs tracking-wide transition-colors duration-300 relative group cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
            isLight
              ? 'border-brand-yellow/60 text-brand-yellow-dark hover:bg-brand-yellow hover:text-brand-blue-dark'
              : 'border-brand-yellow/50 text-brand-yellow hover:bg-brand-yellow hover:text-brand-blue-dark'
          }`}
        >
          <i className="ri-phone-line text-xs" />
          {link.label}
        </a>
      </li>
    );
  }

  const isMinistry = link.path === '/ministry-connections';

  if (isMinistry) {
    return (
      <li>
        <Link
          to={link.path}
          className="relative group cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5"
        >
          <span
            className={`font-body font-medium text-[11px] 2xl:text-xs tracking-wide transition-colors duration-300 relative ${
              isLight
                ? 'text-brand-blue-dark group-hover:text-brand-purple'
                : 'text-white group-hover:text-brand-yellow'
            } ${location.pathname === link.path ? (isLight ? 'text-brand-purple' : 'text-brand-yellow') : ''}`}
          >
            {link.label}
            <span
              className={`absolute -bottom-1 left-0 h-0.5 bg-brand-yellow transition-all duration-300 ${
                location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
              }`}
            />
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-brand-yellow text-[#0F0E0C] font-body font-black text-[9px] uppercase tracking-wide leading-none animate-pulse">
            Free
          </span>
        </Link>
      </li>
    );
  }

  return (
    <li>
      <Link
        to={link.path}
        className={`font-body font-medium text-[11px] 2xl:text-xs tracking-wide transition-colors duration-300 relative group cursor-pointer whitespace-nowrap ${
          isLight
            ? 'text-brand-blue-dark hover:text-brand-purple'
            : 'text-white hover:text-brand-yellow'
        } ${location.pathname === link.path ? (isLight ? 'text-brand-purple' : 'text-brand-yellow') : ''}`}
      >
        {link.label}
        <span
          className={`absolute -bottom-1 left-0 h-0.5 bg-brand-yellow transition-all duration-300 ${
            location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
          }`}
        />
      </Link>
    </li>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileProgOpen, setMobileProgOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileProgOpen(false);
  }, [location.pathname]);

  const isTransparentPage = location.pathname === '/';
  const isLight = scrolled || !isTransparentPage;

  const programsLink = NAV_LINKS.find((l) => l.label === 'Programs');
  const isProgramsActive = programsLink?.children?.some((c) => c.path === location.pathname) ?? false;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isLight ? 'navbar-scrolled' : 'bg-transparent'
      }`}
    >
      <nav className="flex items-center px-4 xl:px-8 py-3 gap-6">
        {/* Logo — always left */}
        <Link to="/" className="flex-shrink-0 cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/979cb23d-2376-41a4-bad4-0dbbe3d67aab/448056ff-3260-4ef2-af6a-d8abb07950e9_Attachment-1.png?v=eaaf18ff7e00f7e3272d34bab21b0a96"
            alt="Creative Institute"
            className={`h-10 w-auto object-contain transition-all duration-300 ${isLight ? '' : 'brightness-0 invert'}`}
          />
        </Link>

        {/* Desktop Nav — centered, spreads across */}
        <ul className="hidden xl:flex items-center justify-center gap-3 2xl:gap-5 flex-1">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <DropdownNavItem key={link.label} link={link} isLight={isLight} />
            ) : (
              <SimpleNavItem key={link.path} link={link} isLight={isLight} />
            )
          )}
        </ul>

        {/* CTA Buttons — far right */}
        <div className="hidden xl:flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => {
              const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
              if (btn) btn.click();
            }}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full font-body font-semibold text-[11px] 2xl:text-xs tracking-wide transition-all duration-300 cursor-pointer border-2 ${
              isLight
                ? 'border-brand-yellow text-brand-yellow-dark hover:bg-brand-yellow hover:text-brand-blue-dark'
                : 'border-brand-yellow/70 text-brand-yellow hover:bg-brand-yellow hover:text-brand-blue-dark'
            }`}
          >
            Free Consultation
          </button>
          <Link
            to="/client-portal"
            className={`whitespace-nowrap px-4 py-1.5 rounded-full font-body font-semibold text-[11px] 2xl:text-xs tracking-wide transition-all duration-300 cursor-pointer border-2 ${
              isLight
                ? 'border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white'
                : 'border-white/60 text-white hover:border-brand-yellow hover:text-brand-yellow'
            }`}
          >
            Work With Us
          </Link>
          <Link
            to="/enroll"
            className="enroll-pulse whitespace-nowrap px-4 py-1.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-semibold text-[11px] 2xl:text-xs tracking-wide hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer"
          >
            Enroll Now
          </Link>
        </div>

        {/* Mobile: logo left, hamburger right */}
        <div className="xl:hidden ml-auto">
          <button
            className={`w-8 h-8 flex flex-col justify-center gap-1.5 cursor-pointer ${
              isLight ? 'text-brand-blue-dark' : 'text-white'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`xl:hidden overflow-hidden transition-all duration-500 bg-white border-t border-brand-purple/10 ${
          menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <ul className="flex flex-col px-6 py-5 gap-1">
          {/* Home */}
          <li>
            <Link
              to="/"
              className={`block py-2.5 font-body font-medium text-sm transition-colors cursor-pointer ${
                location.pathname === '/' ? 'text-brand-purple' : 'text-brand-blue-dark hover:text-brand-purple'
              }`}
            >
              Home
            </Link>
          </li>

          {/* Programs dropdown (mobile inline) */}
          <li>
            <button
              onClick={() => setMobileProgOpen((v) => !v)}
              className={`w-full flex items-center justify-between py-2.5 font-body font-medium text-sm transition-colors cursor-pointer ${
                isProgramsActive ? 'text-brand-purple' : 'text-brand-blue-dark hover:text-brand-purple'
              }`}
            >
              Programs
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${mobileProgOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${mobileProgOpen ? 'max-h-40' : 'max-h-0'}`}>
              <div className="pl-4 pb-2 flex flex-col gap-1">
                {programsLink?.children?.map((child) => (
                  <Link
                    key={child.path}
                    to={child.path}
                    className={`block py-2 font-body text-sm transition-colors cursor-pointer ${
                      location.pathname === child.path ? 'text-brand-purple font-semibold' : 'text-gray-500 hover:text-brand-purple'
                    }`}
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            </div>
          </li>

          {/* Remaining links */}
          {NAV_LINKS.filter((l) => !l.children && l.path !== '/').map((link) => (
            <li key={link.path}>
              {link.path!.startsWith('tel:') ? (
                <a
                  href={link.path!}
                  className="inline-flex items-center gap-2 py-2.5 font-body font-semibold text-sm text-brand-yellow-dark cursor-pointer"
                >
                  <i className="ri-phone-line text-sm" />
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.path!}
                  className={`inline-flex items-center gap-2 py-2.5 font-body font-medium text-sm transition-colors cursor-pointer ${
                    location.pathname === link.path ? 'text-brand-purple' : 'text-brand-blue-dark hover:text-brand-purple'
                  }`}
                >
                  {link.label}
                  {link.path === '/ministry-connections' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-yellow text-[#0F0E0C] font-body font-black text-[9px] uppercase tracking-wide leading-none">
                      Free
                    </span>
                  )}
                </Link>
              )}
            </li>
          ))}

          <li className="flex flex-col gap-3 pt-3 mt-1 border-t border-gray-100">
            <button
              onClick={() => {
                setMenuOpen(false);
                setTimeout(() => {
                  const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
                  if (btn) btn.click();
                }, 200);
              }}
              className="inline-block whitespace-nowrap px-5 py-2.5 rounded-full border-2 border-brand-yellow text-brand-yellow-dark font-body font-semibold text-sm text-center cursor-pointer hover:bg-brand-yellow hover:text-brand-blue-dark transition-colors w-full"
            >
              Free Consultation
            </button>
            <Link
              to="/client-portal"
              className="inline-block whitespace-nowrap px-5 py-2.5 rounded-full border-2 border-brand-purple text-brand-purple font-body font-semibold text-sm text-center cursor-pointer"
            >
              Work With Us
            </Link>
            <Link
              to="/enroll"
              className="enroll-pulse inline-block whitespace-nowrap px-5 py-2.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-semibold text-sm text-center cursor-pointer"
            >
              Enroll Now
            </Link>
          </li>
        </ul>
      </div>
    </header>
  );
}
